
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useCrmUsers } from '@/hooks/crm/useCrmUsers';
import { useReferralTemplates } from '@/hooks/useReferralTemplates';
import { useCrmSettings } from '@/hooks/useCrmSettings';
import CRMAddUserForm from './CRMAddUserForm';
import CSVUploadIntegration from '../CSVUploadIntegration';
import BulkInvitationManager from './BulkInvitationManager';
import CRMSettings from './CRMSettings';
import SendReferralDialog from './SendReferralDialog';
import CRMDashboardHeader from './dashboard/CRMDashboardHeader';
import CRMNavigationPanel from './dashboard/CRMNavigationPanel';
import CRMUsersPanel from './dashboard/CRMUsersPanel';
import { CRMUser, CRMUserFormData } from '@/types/crm';

const CRMDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [sendingToUserId, setSendingToUserId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'users' | 'upload' | 'bulk' | 'settings'>('users');
  
  const {
    filteredUsers,
    selectedUser,
    setSelectedUser,
    isLoading,
    searchQuery,
    setSearchQuery,
    processingUsers,
    loadUsers,
    addUser,
    sendReferralEmail
  } = useCrmUsers();
  
  const { templates } = useReferralTemplates();
  const { settings, saveSettings } = useCrmSettings();
  
  const handleAddUser = async (userData: CRMUserFormData) => {
    setIsSubmitting(true);
    try {
      await addUser(userData);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleViewUser = (user: CRMUser) => {
    setSelectedUser(user);
  };
  
  const handleBackToList = () => {
    setSelectedUser(null);
  };
  
  const handleSendReferral = (userId: string) => {
    setSendingToUserId(userId);
    setIsSendDialogOpen(true);
  };
  
  const handleSendReferralConfirm = async (templateId: string, templateName: string) => {
    if (!sendingToUserId) return;
    
    setIsSubmitting(true);
    try {
      await sendReferralEmail(sendingToUserId, templateId, templateName);
    } catch (error) {
      console.error('Error sending referral:', error);
    } finally {
      setIsSubmitting(false);
      setSendingToUserId(null);
    }
  };
  
  const handleSaveSettings = async (newSettings: any) => {
    await saveSettings(newSettings);
  };
  
  const newUsers = filteredUsers.filter(user => user.status === 'new');
  const contactedUsers = filteredUsers.filter(user => user.status === 'contacted');
  const activeUsers = filteredUsers.filter(user => user.status === 'active');
  
  return (
    <div className="space-y-6">
      <Card>
        <CRMDashboardHeader
          onRefresh={loadUsers}
          onToggleAddForm={() => setShowAddForm(!showAddForm)}
          showAddForm={showAddForm}
        />
        
        <CardContent>
          <CRMNavigationPanel
            activePanel={activePanel}
            onPanelChange={setActivePanel}
          />
          
          {showAddForm && (
            <Card className="mb-6">
              <CardContent>
                <CRMAddUserForm onAddUser={handleAddUser} />
              </CardContent>
            </Card>
          )}
          
          {activePanel === 'users' && (
            <CRMUsersPanel
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              filteredUsers={filteredUsers}
              newUsers={newUsers}
              contactedUsers={contactedUsers}
              activeUsers={activeUsers}
              selectedUser={selectedUser}
              onViewUser={handleViewUser}
              onSendReferral={handleSendReferral}
              processingUsers={processingUsers}
              onBackToList={handleBackToList}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          )}
          
          {activePanel === 'upload' && (
            <CSVUploadIntegration onSaved={loadUsers} />
          )}
          
          {activePanel === 'bulk' && (
            <BulkInvitationManager maxInvites={settings.maxDailyInvites} />
          )}
          
          {activePanel === 'settings' && (
            <CRMSettings 
              onSave={handleSaveSettings}
              initialSettings={{ bulkInviteTime: settings.bulkInviteTime }}
            />
          )}
        </CardContent>
      </Card>
      
      <SendReferralDialog
        isOpen={isSendDialogOpen}
        onClose={() => setIsSendDialogOpen(false)}
        onSend={handleSendReferralConfirm}
        isSubmitting={isSubmitting}
        templates={templates}
      />
    </div>
  );
};

export default CRMDashboard;

